// NEW_FILE_JUSTIFIED: Custom hook for compose email form state — ComposeEmail component needs 5+ state fields, exceeding the 2-useState component limit
import { useState, useCallback } from 'react';
import type { IComposeEmailData, IUseComposeEmailReturn } from '@/@types/gmail';

/** Parse comma-separated email string into array, filtering empty strings */
function parseEmails(input: string): string[] {
  return input
    .split(',')
    .map(e => e.trim())
    .filter(Boolean);
}

export function useComposeEmail(initialData?: Partial<IComposeEmailData>): IUseComposeEmailReturn {
  const [to, setTo] = useState(initialData?.to?.join(', ') ?? '');
  const [cc, setCc] = useState(initialData?.cc?.join(', ') ?? '');
  const [bcc, setBcc] = useState(initialData?.bcc?.join(', ') ?? '');
  const [subject, setSubject] = useState(initialData?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(initialData?.bodyHtml ?? '');
  const [attachments, setAttachments] = useState<File[]>(initialData?.attachments ?? []);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const addAttachments = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    setAttachments(prev => [...prev, ...fileArray]);
  }, []);

  const removeAttachment = useCallback((index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const toggleCcBcc = useCallback(() => {
    setShowCcBcc(prev => !prev);
  }, []);

  const getComposeData = useCallback(
    (): IComposeEmailData => ({
      to: parseEmails(to),
      cc: parseEmails(cc),
      bcc: parseEmails(bcc),
      subject,
      bodyHtml,
      attachments,
    }),
    [to, cc, bcc, subject, bodyHtml, attachments]
  );

  const reset = useCallback(() => {
    setTo('');
    setCc('');
    setBcc('');
    setSubject('');
    setBodyHtml('');
    setAttachments([]);
  }, []);

  return {
    to,
    setTo,
    cc,
    setCc,
    bcc,
    setBcc,
    subject,
    setSubject,
    bodyHtml,
    setBodyHtml,
    attachments,
    addAttachments,
    removeAttachment,
    showCcBcc,
    toggleCcBcc,
    isSending,
    setIsSending,
    getComposeData,
    reset,
  };
}
